import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FileUpload } from '../../../../../components/ui/FileUpload';
import { Button } from '../../../../../components/ui/button';
import { FileText, X, Info, AlertTriangle, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { Device } from '../../../types/device';
import { Card, Spin } from 'antd';
import { CardContent } from '../../../../../components/ui/card';
import { getPreSignedUrlToUploadFirmware, updateFirmware } from '../../../../../redux/device/saga';
import { Notification } from '../../../../../components/ui/custom-notification';
import { createUpdateFirmwareUpgradeHistory } from '../../../../../redux/Project/systemSettingSaga';
import { addFirmwareUpdateDevice, fetchDevices, removeFirmwareUpdateDevice, startFirmwareUpdatePolling, setFirmwareUpdateProgress, clearFirmwareUpdateProgress } from '../../../../../redux/device/actions';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../../../../components/ui/dialog';

interface FirmwarePageProps {
  device: Device;
  refetchDevice: () => void;
  startAutoRefresh?: () => void;
  stopAutoRefresh?: () => void;
  selectedFile: File | null;
  setSelectedFile: (file: File | null) => void;
}

export const FirmwarePage: React.FC<FirmwarePageProps> = ({ device, refetchDevice, startAutoRefresh, stopAutoRefresh, selectedFile, setSelectedFile }) => {
  const dispatch = useDispatch();
  const firmwareUpdateDevices = useSelector((state: any) => state.deviceData?.firmwareUpdateDevice || []);

  // Get current user information for audit logging
  const currentUser = useSelector((state: any) => state.auth?.user);
  const userPermissions = useSelector((state: any) => state.userManagement?.userPermissions?.data);
  const users = useSelector((state: any) => state.userManagement?.users || []);


  const [isLoading, setIsLoading] = useState(false);
  const [fileProcessType, setFileProcessType] = useState<string | null>(null);
  const [fileBuildType, setFileBuildType] = useState<string | null>(null); // Store file's buildType (can/serial)
  const [firwareUpdateSuccess, setFirwareUpdateSuccess] = useState<string | null>(null);
  const [firwareUpdateError, setFirwareUpdateError] = useState<string | null>(null);
  const [isFirmwareUpdating, setIsFirmwareUpdating] = useState(false);
  const [hasFileBeenValidated, setHasFileBeenValidated] = useState(false);
  const [validdationfileError, setValiddationfileError] = useState<any | null>(null);
  const [isProgressCardDismissed, setIsProgressCardDismissed] = useState(false);

  // Get firmware update progress from Redux
  const firmwareUpdateProgress = useSelector((state: any) =>
    state.deviceData?.firmwareUpdateProgress?.[device.id] || null
  );

  // console.log('firmwareUpdateProgress', firmwareUpdateProgress);
  // Monitor firmware update progress from Redux
  // This ensures progress persists when navigating away and coming back
  useEffect(() => {
    if (firmwareUpdateProgress) {
      const status = firmwareUpdateProgress.status;
      if (status === 'initiated' || status === 'in_progress') {
        setIsFirmwareUpdating(true);
        setFirwareUpdateSuccess('initiated');
        setIsLoading(false);
        setFirwareUpdateError(null);
        setIsProgressCardDismissed(false);
        //   console.log('firmwareUpdateProgress initiated', firmwareUpdateProgress);
      } else if (status === 'completed') {
        // Restore success state from Redux when switching tabs
        setIsFirmwareUpdating(false);
        setIsLoading(false);
        setFirwareUpdateSuccess('success');
        setFirwareUpdateError(null);
        dispatch(fetchDevices({}));
        //   console.log('firmwareUpdateProgress completed', firmwareUpdateProgress);
      } else if (status === 'failed') {
        setIsFirmwareUpdating(false);
        setIsLoading(false);
        setFirwareUpdateSuccess('failed');
        setFirwareUpdateError(firmwareUpdateProgress.error || 'Firmware update failed');
      }
    } else {
      // No progress data - reset states if not in Redux tracking
      if (!firmwareUpdateDevices.includes(device.id)) {
        setIsFirmwareUpdating(false);
        // Don't reset success state here - let it persist if it was set from Redux
        // Only reset loading if success state is null
        if (firwareUpdateSuccess === null) {
          setIsLoading(false);
        }
      } else {
        // Device is in tracking but no progress yet - might be initializing
        // Keep current state to preserve success message if it was previously completed
      }
    }
  }, [firmwareUpdateProgress, device.id, firmwareUpdateDevices]);



  useEffect(() => {
    if (firmwareUpdateProgress) {
      const status = firmwareUpdateProgress.status;

      if (status === 'initiated' || status === 'in_progress') {
        setIsFirmwareUpdating(true);
        setFirwareUpdateSuccess('initiated');
        setIsLoading(false);
        setFirwareUpdateError(null);
        setIsProgressCardDismissed(false);
        //  console.log('firmwareUpdateProgress initiated', firmwareUpdateProgress);
      } else if (selectedFile && status === 'completed') {
        // Restore success state from Redux when switching tabs
        setIsFirmwareUpdating(false);
        setIsLoading(false);
        setFirwareUpdateSuccess(null);
        setFirwareUpdateError(null);
        // // dispatch(fetchDevices({}));
        // console.log('firmwareUpdateProgress completed', firmwareUpdateProgress);
        // // Clear validation error since file is now valid
        // setValiddationfileError(null);


        setSelectedFile(null);
        setFirmwareVersionFromFile(null);
        setFileBuildType(null);
        setHasFileBeenValidated(false);
        setValiddationfileError(null);
        setIsProgressCardDismissed(false);
        // Clear previous firmware update progress for this device when selecting a new file
        dispatch(clearFirmwareUpdateProgress(device.id));
        // Clear previous success/error states
        setFirwareUpdateSuccess(null);
        setFirwareUpdateError(null);

      } else if (!selectedFile && status === 'failed') {
        setIsFirmwareUpdating(false);
        setIsLoading(false);
        setFirwareUpdateSuccess('failed');
        setFirwareUpdateError(firmwareUpdateProgress.error || 'Firmware update failed');
        // // Clear validation error since file is now valid
        // setValiddationfileError(null);

        // // Clear previous firmware update progress for this device when selecting a new file
        // dispatch(clearFirmwareUpdateProgress(device.id));

        // // Clear previous success/error states
        // setFirwareUpdateSuccess(null);
        // setFirwareUpdateError(null);

      }
    } else {
      // No progress data - reset states if not in Redux tracking
      if (!firmwareUpdateDevices.includes(device.id)) {
        setIsFirmwareUpdating(false);
        // Don't reset success state here - let it persist if it was set from Redux
        // Only reset loading if success state is null
        if (firwareUpdateSuccess === null) {
          setIsLoading(false);
        }
      } else {
        // Device is in tracking but no progress yet - might be initializing
        // Keep current state to preserve success message if it was previously completed
      }
    }
  }, []);
  // Helper function to normalize platform value for comparison
  const normalizePlatform = (platform: string | undefined): string | null => {
    if (!platform) return null;
    const lowerPlatform = platform.toLowerCase();
    if (lowerPlatform.includes('imx8a') || lowerPlatform.includes('alpha') || lowerPlatform.includes('8a')) return 'imx_8a';
    if (lowerPlatform.includes('imx8b') || lowerPlatform.includes('beta') || lowerPlatform.includes('8b')) return 'imx_8b';
    if (lowerPlatform.includes('imx8ivy') || lowerPlatform.includes('ivy') || lowerPlatform.includes('8ivy')) return 'imx_8ivy';
    // Also check for direct matches like "8a", "8b", "8ivy"
    if (lowerPlatform === '8a' || lowerPlatform === 'imx_8a') return 'imx_8a';
    if (lowerPlatform === '8b' || lowerPlatform === 'imx_8b') return 'imx_8b';
    if (lowerPlatform === '8ivy' || lowerPlatform === 'imx_8ivy') return 'imx_8ivy';
    return null;
  };

  // Helper function to format platform value for display
  const formatPlatform = (platform: string | undefined): string => {
    if (!platform) return '-';
    const normalized = normalizePlatform(platform);
    if (normalized) return normalized;
    return platform; // Return original if no match
  };

  // Helper function to get build type from weld_type
  const getBuildType = (processType: string | undefined, weldType: string | undefined): string | null => {
    if (processType?.toLowerCase() !== 'welding') return null;
    if (!weldType) return null;
    const lowerWeldType = weldType.toLowerCase();
    if (lowerWeldType.includes('m500')) return 'CAN (M500)';
    if (lowerWeldType.includes('p600z') || lowerWeldType.includes('p625')) return 'Serial (P600Z / P625)';
    return null;
  };

  // Get device information
  const processType = device.deviceData?.process_type || device.deviceData?.processType || '-';
  const platform = formatPlatform(device.deviceData?.platform || '-');
  const buildType = getBuildType(processType, device.deviceData?.weld_type || '-');

  // Get environment - check deviceData or use default
  const environment = process.env.REACT_APP_CUSTOM_NODE_ENV || 'dev';

  // Get connection type from buildType
  const connectionType = buildType?.includes('Serial') ? 'serial' : buildType?.includes('CAN') ? 'can' : undefined;

  // Helper function to parse version string (e.g., "v4.1.0" or "4.1.0" -> [4, 1, 0])
  const parseVersion = (version: string): number[] => {
    // Remove 'v' prefix if present and split by '.'
    const cleaned = version.replace(/^v/i, '').trim();
    return cleaned.split('.').map(num => parseInt(num, 10) || 0);
  };

  // Helper function to compare versions (returns true if version1 >= version2)
  // For minimum version checks: if major version matches, allow if minor.patch combined is significant
  // Example: 4.0.20.15 vs 4.1.0 - major (4) matches, but 0.20 < 1.0, so it's less
  // But we can be more lenient: if major matches and patch is high, consider it acceptable
  const compareVersions = (version1: string, version2: string): boolean => {
    const v1 = parseVersion(version1);
    const v2 = parseVersion(version2);

    // Compare major version first
    if (v1[0] > v2[0]) return true;
    if (v1[0] < v2[0]) return false;

    // Major versions match - compare minor.patch
    // For versions like 4.0.20.15, compare 4.0.20 against 4.1.0
    const minor1 = v1[1] || 0;
    const minor2 = v2[1] || 0;
    const patch1 = v1[2] || 0;
    const patch2 = v2[2] || 0;

    // Compare minor version
    if (minor1 > minor2) return true;
    if (minor1 < minor2) return false;

    // Minor versions match - compare patch
    if (patch1 >= patch2) return true;

    return false;
  };

  // Minimum firmware requirements
  const MIN_WELDING_FIRMWARE = '4.1.1';
  const MIN_COATING_FIRMWARE = '1.0.3';

  // Check if device's current firmware version meets minimum requirement
  const getDeviceMinRequiredVersion = (): string | null => {
    const deviceProcessType = processType?.toLowerCase();
    if (deviceProcessType === 'welding') {
      return MIN_WELDING_FIRMWARE;
    } else if (deviceProcessType === 'coating') {
      return MIN_COATING_FIRMWARE;
    }
    return null;
  };

  const deviceMinRequiredVersion = getDeviceMinRequiredVersion();
  const deviceCurrentVersion = device.EdgeFWVersion;

  // Check if device is running a version that supports firmware updates
  const isDeviceVersionSupported = (): boolean => {
    if (!processType || processType === '-') {
      //  console.log('processType', processType);
      //  console.log('deviceCurrentVersion', deviceCurrentVersion);
      //  console.log('deviceMinRequiredVersion', deviceMinRequiredVersion);
      return true;
    }
    if (!deviceCurrentVersion || !deviceMinRequiredVersion) {
      return true; // If version is unknown or failed, allow update attempt
    }
    return compareVersions(deviceCurrentVersion, deviceMinRequiredVersion);
  };

  const deviceVersionSupported = isDeviceVersionSupported();

  // State to track firmware version from selected file
  const [firmwareVersionFromFile, setFirmwareVersionFromFile] = useState<string | null>(null);

  // Check if selected firmware file is compatible (based on file version, not device version)
  const isFirmwareCompatible = (fileVersion: string | null): boolean => {
    if (!fileVersion) return true; // If no file selected, assume compatible

    const deviceProcessType = processType?.toLowerCase();

    if (fileProcessType === 'welding') {
      return compareVersions(fileVersion, MIN_WELDING_FIRMWARE);
    } else if (fileProcessType === 'coating') {
      return compareVersions(fileVersion, MIN_COATING_FIRMWARE);
    }

    // If process type is unknown, allow upload (assume compatible)
    return true;
  };

  const firmwareCompatible = isFirmwareCompatible(firmwareVersionFromFile);
  const minRequiredVersion = fileProcessType === 'welding'
    ? MIN_WELDING_FIRMWARE
    : fileProcessType === 'coating'
      ? MIN_COATING_FIRMWARE
      : null;

  const isOnline = device.communication_status === 'Online';
  // Validation function for firmware filename
  const validateFirmwareFilename = (
    fileName: string,
    environment: string,
    processType: string,
    deviceType: string,
    connectionType?: string
  ): { isValid: boolean; error?: string; firmwareVersion?: string } => {
    const nameWithoutExt = fileName.replace(/\.zip$/i, '');
    const parts = nameWithoutExt.split('-');

    if (!fileName.toLowerCase().endsWith('.zip')) {
      return { isValid: false, error: 'Firmware file must be a .zip file' };
    }

    // ProcessType enum values
    const ProcessTypeEnum = {
      COATING: 'coating',
      WELDING: 'welding'
    };

    // ConnectionType enum values
    const ConnectionTypeEnum = {
      CAN: 'can',
      SERIAL: 'serial'
    };


    // if(firmwareCompatible) {
    //   Notification.error('Firmware Version Incompatible', 'The selected firmware version does not meet the minimum requirement. Please select a firmware version that meets the minimum requirement.');
    //   return { isValid: false, error: 'Firmware version is compatible' };
    // }
    // if (processType?.toLowerCase() === ProcessTypeEnum.COATING) {
    //   if (parts.length < 4) {
    //     return { isValid: false, error: 'Invalid coating firmware filename format. Expected: data360-coating-{env}-v{version}.zip' };
    //   }
    //   if (parts[0] !== 'data360' || parts[1] !== 'coating') {
    //     return { isValid: false, error: 'Coating firmware filename must start with "data360-coating-"' };
    //   }
    //   if (parts[2] !== environment) {
    //     return { isValid: false, error: `Firmware environment (${parts[2]}) does not match current environment (${environment})` };
    //   }
    //   if (!parts[3].startsWith('v')) {
    //     return { isValid: false, error: 'Version must start with "v"' };
    //   }
    // } else if (processType?.toLowerCase() === ProcessTypeEnum.WELDING) {
    let expectedPrefix = 'data360';
    let envIndex = 1;
    let buildType = 'serial';
    let firmwareProcessType: string | null = null;

    // Detect firmware process type from filename
    if (parts[0] === 'data360' && parts[1] === 'can') {
      buildType = 'can';
      envIndex = 2;
      firmwareProcessType = 'welding'; // CAN is for welding devices
    }
    else if (parts[0] === 'data360' && parts[1] === 'coating') {
      buildType = 'serial';
      envIndex = 2;
      firmwareProcessType = 'coating'; // Explicitly coating firmware
    }
    else if (parts[0] === 'data360') {
      // Standard welding firmware format: data360-{env}-v{version}-{platform}
      firmwareProcessType = 'welding'; // Default to welding if not explicitly coating
    }

    // Check if firmware process type matches device process type
    const deviceProcessTypeLower = processType?.toLowerCase();
    if (firmwareProcessType && deviceProcessTypeLower && deviceProcessTypeLower !== '-' &&
      firmwareProcessType !== deviceProcessTypeLower) {
      return {
        isValid: false,
        error: `Process type mismatch: The firmware file is for ${firmwareProcessType} devices, but this device is a ${deviceProcessTypeLower} device. Please select a firmware file that matches the device type.`
      };
    }
    setFileProcessType(firmwareProcessType);
    // Store file's buildType (can/serial) for use in payload
    setFileBuildType(buildType);
    if (parts[envIndex] !== environment) {
      return { isValid: false, error: `Firmware environment (${parts[envIndex]}) does not match current environment (${environment})` };
    }

    const versionIndex = envIndex + 1;
    if (!parts[versionIndex] || !parts[versionIndex].startsWith('v')) {
      return { isValid: false, error: 'Invalid version format. Version must start with "v"' };
    }

    // Extract firmware version from filename (remove 'v' prefix)
    const firmwareVersion = parts[versionIndex].replace(/^v/i, '');

    // For welding firmware, platform is in filename; for coating, it might not be
    let firmwarePlatform: string | null = null;
    if (firmwareProcessType === 'welding') {
      const platformIndex = versionIndex + 1;
      if (!parts[platformIndex]) {
        return { isValid: false, error: 'Platform information missing in filename' };
      }
      firmwarePlatform = normalizePlatform(parts[platformIndex]);
      if (!firmwarePlatform) {
        return { isValid: false, error: `Invalid platform in filename: ${parts[platformIndex]}. Platform must be 8a, 8b, or 8ivy.` };
      }
    } else if (firmwareProcessType === 'coating') {
      // Coating firmware format: data360-coating-{env}-v{version}.zip (no platform in filename)
      // Platform validation will be done against device platform only
      firmwarePlatform = null; // Coating firmware doesn't specify platform in filename
    }

    // Validate device platform
    const devicePlatformNormalized = normalizePlatform(deviceType);
    if (!devicePlatformNormalized) {
      return { isValid: false, error: `Device platform (${deviceType}) is not recognized. Supported platforms: 8a, 8b, 8ivy.` };
    }

    // Validate platform match
    if (firmwarePlatform) {
      // Welding firmware: platform must match
      if (firmwarePlatform !== devicePlatformNormalized) {
        return {
          isValid: false,
          error: `Platform mismatch: Firmware file is for platform ${firmwarePlatform}, but this device is platform ${devicePlatformNormalized}. Please select a firmware file that matches the device platform.`
        };
      }
    } else {
      // Coating firmware: device must have valid platform (8a, 8b, or 8ivy)
      // Platform is already validated above, so this is just a check that device has valid platform
      // No need to match firmware platform since coating firmware doesn't specify it
    }

    // For welding devices, validate weld type (CAN/Serial) - only validate if device has weld_type
    // If device weld_type is null/empty, allow any file (CAN or Serial) and use file's buildType
    if (firmwareProcessType === 'welding' && deviceProcessTypeLower === 'welding') {
      // Only validate if device has weld type (buildType is not null/empty)
      // If device weld_type is null/empty, skip validation and allow any file
      if (connectionType) {
        // Device has weld type (CAN or Serial) - apply strict validation
        if (connectionType === ConnectionTypeEnum.CAN && buildType !== 'can') {
          // Device is CAN (M500) but firmware is Serial - block upload
          return {
            isValid: false,
            error: `Weld type mismatch: This device uses CAN (M500) weld type, but the firmware file is for Serial weld type. CAN devices can only upload CAN firmware files (data360-can-...). Please select a CAN firmware file.`
          };
        } else if (connectionType === ConnectionTypeEnum.SERIAL && buildType !== 'serial') {
          // Device is Serial (P600Z/P625) but firmware is CAN - block upload
          return {
            isValid: false,
            error: `Weld type mismatch: This device uses Serial (P600Z/P625) weld type, but the firmware file is for CAN weld type. Serial devices can only upload Serial firmware files (data360-...). Please select a Serial firmware file.`
          };
        }
      }
      // If connectionType is undefined/null/empty, device has no weld_type - allow any file (CAN or Serial)
    } else if (firmwareProcessType === 'coating' && deviceProcessTypeLower === 'coating') {
      // For coating devices, weld type is not applicable - no validation needed
      // Coating firmware doesn't have weld type distinction
    }

    // Check firmware version compatibility
    const MIN_WELDING_FIRMWARE = '4.1.1';
    const MIN_COATING_FIRMWARE = '1.0.3';

    let minRequiredVersion: string | null = null;
    if (firmwareProcessType === 'welding') {
      minRequiredVersion = MIN_WELDING_FIRMWARE;
    } else if (firmwareProcessType === 'coating') {
      minRequiredVersion = MIN_COATING_FIRMWARE;
    }

    // Check if firmware version meets minimum requirement
    // Compare only major.minor.patch (first 3 parts), ignoring build numbers
    if (minRequiredVersion) {
      const v1 = parseVersion(firmwareVersion);
      const v2 = parseVersion(minRequiredVersion);

      // Compare major version first
      if (v1[0] < v2[0]) {
        return {
          isValid: false,
          error: `${firmwareProcessType === 'welding' ? 'Welding' : 'Coating'} device: Firmware version must be ≥ ${minRequiredVersion}. Selected file version: ${firmwareVersion}`,
          firmwareVersion: firmwareVersion
        };
      }

      // If major version matches, compare minor.patch
      if (v1[0] === v2[0]) {
        const minor1 = v1[1] || 0;
        const minor2 = v2[1] || 0;
        const patch1 = v1[2] || 0;
        const patch2 = v2[2] || 0;

        // Compare minor.patch as combined value for more lenient comparison
        // This allows 4.0.20 to be considered if it's "close enough" to 4.1.0
        // Or we can be strict: minor must be >= required minor
        if (minor1 < minor2 || (minor1 === minor2 && patch1 < patch2)) {
          return {
            isValid: false,
            error: `${firmwareProcessType === 'welding' ? 'Welding' : 'Coating'} device: Firmware version must be ≥ ${minRequiredVersion}. Selected file version: ${firmwareVersion}`,
            firmwareVersion: firmwareVersion
          };
        }
      }
    }

    // Return validation result
    return {
      isValid: true,
      firmwareVersion: firmwareVersion
    };
  };

  // Validate selected file when tab becomes active or when file is selected
  useEffect(() => {
    if (selectedFile?.name) {
      const validation = validateFirmwareFilename(
        selectedFile.name,
        environment,
        processType,
        platform,
        connectionType
      );

      if (!validation.isValid) {
        setValiddationfileError(validation);
        setHasFileBeenValidated(false);
        setFirmwareVersionFromFile(null);
      } else {
        // Store firmware version from file
        if (validation.firmwareVersion) {
          setFirmwareVersionFromFile(validation.firmwareVersion);
        }
        // Clear validation error since file is now valid
        setValiddationfileError(null);
        setHasFileBeenValidated(true);
      }
    }
  }, [selectedFile, environment, processType, platform, connectionType]);

  const handleFileSelect = (files: FileList | null) => {
    if (files && files.length > 0) {
      const file = files[0];
      setFirwareUpdateSuccess(null);
      setFirwareUpdateError(null);
      // Validate the firmware file
      const validation = validateFirmwareFilename(
        file.name,
        environment,
        processType,
        platform,
        connectionType
      );

      // console.log('validation', validation);
      if (!validation.isValid) {
        setValiddationfileError(validation);
        setHasFileBeenValidated(false);
        // Clear selected file state
        // setSelectedFile(null);
        setSelectedFile(file);
        setFirmwareVersionFromFile(null);

        // Show error notification
        Notification.error('Invalid Firmware File', validation.error || 'Invalid firmware file. Please select a valid file.');

        // Reset file input to allow selecting the same file again
        // This ensures the onChange event will fire even if the same file is selected
        setTimeout(() => {
          const fileInputs = document.querySelectorAll('input[type="file"]');
          fileInputs.forEach((input) => {
            (input as HTMLInputElement).value = '';
          });
        }, 0);

        return file;
      }

      // Store firmware version from file
      if (validation.firmwareVersion) {
        setFirmwareVersionFromFile(validation.firmwareVersion);
      }

      // Clear validation error since file is now valid
      setValiddationfileError(null);

      // Clear previous firmware update progress for this device when selecting a new file
      dispatch(clearFirmwareUpdateProgress(device.id));

      // Clear previous success/error states
      setFirwareUpdateSuccess(null);
      setFirwareUpdateError(null);

      //console.log('file', file);
      setSelectedFile(file);
      setHasFileBeenValidated(true);
      return file;
    }
    return null;
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setFirmwareVersionFromFile(null);
    setFileBuildType(null);
    setFirwareUpdateSuccess(null);
    setFirwareUpdateError(null);
    setHasFileBeenValidated(false);
    setValiddationfileError(null);
    setIsProgressCardDismissed(false);

    // Clear previous firmware update progress for this device when removing file
    dispatch(clearFirmwareUpdateProgress(device.id));
  };

  const handleDismissProgressCard = () => {
    setIsProgressCardDismissed(true);
    // Clear all firmware update states
    setFirwareUpdateSuccess(null);
    setFirwareUpdateError(null);
    setIsFirmwareUpdating(false);
    // Clear firmware update progress from Redux
    dispatch(clearFirmwareUpdateProgress(device.id));
  };

  // Start polling firmware update status using Redux Saga
  const startPollingFirmwareStatus = () => {
    const currentDeviceId = device.id;

    if (!currentDeviceId) {
      console.error('Cannot start polling: device ID is missing');
      return;
    }

    // Add device to Redux tracking
    if (!firmwareUpdateDevices.includes(currentDeviceId)) {
      dispatch(addFirmwareUpdateDevice(currentDeviceId));
    }

    // Start polling via Redux Saga
    dispatch(startFirmwareUpdatePolling(currentDeviceId, device));
  };

  const handleUpdateFirmware = async () => {
    if (!selectedFile) return;

    // Proceed with update (validation already checked for weld type mismatch)
    await proceedWithFirmwareUpdate();
  };

  // Helper function to get current user information for audit logging
  const getCurrentUserInfo = () => {
    // Try to get from userPermissions first
    if (userPermissions?.data?.user_id) {
      return {
        user_id: userPermissions.data.user_id,
        user_name: userPermissions.data.user_name || userPermissions.data.name || currentUser?.username || 'Unknown',
        user_email: userPermissions.data.user_email || userPermissions.data.email || currentUser?.email || null
      };
    }

    // Try to find user in users list by cognitoUserId
    if (currentUser?.sub) {
      const cognitoId = currentUser.sub;
      const foundUser = users.find((user: any) => user.cognitoUserId === cognitoId);
      if (foundUser) {
        return {
          user_id: foundUser.id,
          user_name: foundUser.name || foundUser.user_name || currentUser.username || 'Unknown',
          user_email: foundUser.email || foundUser.user_email || currentUser.email || null
        };
      }
    }

    // Fallback to currentUser from auth
    return {
      user_id: null,
      user_name: currentUser?.username || 'Unknown',
      user_email: currentUser?.email || null
    };
  };

  const proceedWithFirmwareUpdate = async () => {
    if (!selectedFile) return;

    // Clear previous firmware update progress for this device before starting new update
    dispatch(clearFirmwareUpdateProgress(device.id));

    setIsFirmwareUpdating(false);
    // Clear previous success/error states
    setFirwareUpdateSuccess(null);
    setFirwareUpdateError(null);
    // stopAutoRefresh?.();
    setIsLoading(true);

    // Record start time for upgrade history
    const upgradeStartedAt = new Date().toISOString();

    try {
      // Get deviceType and connectionType from deviceData or use defaults
      const deviceType = device.id;

      // Prepare payload for presigned URL request
      // Use file's buildType for connectionType
      // If device weld_type is null/empty, use file's buildType (fileBuildType)
      // If device has weld_type, fileBuildType should match it (already validated)
      const connectionTypeForPayload = fileBuildType || 'serial'; // Default to serial if not set

      let payload: any = {
        deviceType: platform,
        fileName: selectedFile.name,
        fileSize: selectedFile.size,
        contentType: selectedFile.type,
        processType: processType
      };

      // Only add connectionType for welding process type
      if (processType?.toLowerCase() === 'welding') {
        payload.connectionType = connectionTypeForPayload === 'serial' ? 'serial' : 'can';
      }

      //console.log('payload', payload);
      // Upload file to S3 using presigned URL (pass single file, not array)
      const fileData = await getPreSignedUrlToUploadFirmware([selectedFile], payload);

      //  console.log('uploadedFile', fileData);

      if (!fileData) {
        Notification.error('Upload Failed', 'Failed to upload firmware file. Please try again.');
        setIsLoading(false);
        return;
      }

      // Get fileUrl from response - check for publicUrl, fileUrl, or presignedUrl
      const fileUrl = fileData.fileKey

      if (!fileUrl) {
        Notification.error('Upload Failed', 'Failed to get file URL after upload.');
        setIsLoading(false);
        return;
      }

      // Update firmware with device ID and file URL
      const updatePayload = {
        deviceIds: [device.id],
        fileUrl: fileUrl
      };

      const response = await updateFirmware(updatePayload);

      // Check for error in response
      if (response && response.data) {
        // Check if data indicates failure
        if (response.data.data === 'Failed' || response.data.error) {
          const errorMessage = response.data.error || 'Firmware update failed';
          Notification.error('Firmware Update Failed', errorMessage);
          setFirwareUpdateError(errorMessage);
          setIsLoading(false);
          return;
        }

        const { successful, results } = response.data;

        if (successful > 0 && results && results.length > 0) {

          // console.log('results', results);

          //dispatch(fetchDevices({}));
          // Extract successful device IDs
          //   const successfulDevices = results
          //     .filter((result: any) => result.status === 'success')
          //     .map((result: any) => result.deviceId);

          //   if (successfulDevices.length > 0) {
          //     const deviceIdsText = successfulDevices.join(', ');
          //     Notification.success(
          //       'Firmware Update Initiated',
          //       `Device ${deviceIdsText} is rebooting. The device data will refresh automatically once it comes back online.`
          //     );
          //   } else {
          //     Notification.success(
          //       'Firmware Update Initiated',
          //       'Device is rebooting. The device data will refresh automatically once it comes back online.'
          //     );
          //   }
          // } else {
          //   Notification.success(
          //     'Firmware Update Initiated',
          //     'Device is rebooting. The device data will refresh automatically once it comes back online.'
          //   );
          // }
        }

        // // Record firmware upgrade history (new 3-table structure)
        // try {
        //   const userInfo = getCurrentUserInfo();

        //   // Extract firmware version from file name or use state
        //   const versionMatch1 = selectedFile.name.match(/v?(\d+\.\d+\.\d+)/i);
        //   const versionMatch2 = selectedFile.name.match(/v?(\d+\.\d+)/i);
        //   const firmwareVersion = firmwareVersionFromFile || 
        //     (versionMatch1 ? versionMatch1[1] : null) || 
        //     (versionMatch2 ? versionMatch2[1] : null) || 
        //     null;

        //   // Get device information
        //   const deviceIp = device.deviceData?.ip_address || device.deviceData?.ip || null;
        //   const deviceUid = device.deviceData?.uid || null;
        //   const deviceWeldType = device.deviceData?.weld_type || device.deviceData?.weldType || null;

        //   // Build payload for firmware upgrade history (new 3-table structure)
        //   const upgradeHistoryPayload = {
        //     device_id: device.id,
        //     ...(deviceUid && { uid: deviceUid }),
        //     ...(deviceIp && { ip_address: deviceIp }),
        //     ...(processType && processType !== '-' && { process_type: processType }),
        //     ...(platform && platform !== '-' && { platform: platform }),
        //     ...(deviceWeldType && { weld_type: deviceWeldType }),
        //     // Use firmware_version for lookup (backend will resolve to firmware_version_id)
        //     ...(firmwareVersion && { firmware_version: firmwareVersion }),
        //     ...(selectedFile.name && { file_name: selectedFile.name }),
        //     ...(fileData.publicUrl && { s23url: fileData.publicUrl }),
        //     status: 'initiated',
        //     date_of_upgrade: upgradeStartedAt,
        //     firmware_version_id: 1, // TODO: get firmware version id from backend
        //     // New 3-table structure fields
        //     upgrade_type: 'manual', // 'manual', 'scheduled', 'OTA', etc.
        //     upgrade_status: 'initiated', // 'initiated', 'in_progress', 'completed', 'failed'
        //     started_at: upgradeStartedAt,
        //     // User detail (JSON object)
        //     user_detail: {
        //       ...(userInfo.user_id && { user_id: userInfo.user_id }),
        //       user_name: userInfo.user_name,
        //       ...(userInfo.user_email && { user_email: userInfo.user_email }),
        //       action: 'manual_upgrade',
        //       initiated_at: upgradeStartedAt
        //     },
        //     // Extra details (JSON object)
        //     extra_details: {
        //       upgrade_method: 'OTA',
        //       connection_type: buildType?.includes('Serial') ? 'serial' : 'can',
        //       file_size: selectedFile.size,
        //       content_type: selectedFile.type,
        //       previous_version: deviceCurrentVersion || null,
        //       ...(fileData.fileKey && { file_key: fileData.fileKey })
        //     }
        //   };

        //   // Call API to create/update firmware upgrade history (non-blocking)
        //   createUpdateFirmwareUpgradeHistory(upgradeHistoryPayload).catch((error) => {
        //     console.error('Failed to record firmware upgrade history:', error);
        //     // Don't show error to user - this is just audit logging
        //   });
        // } catch (historyError) {
        //   console.error('Error preparing firmware upgrade history:', historyError);
        //   // Don't block the main flow if history recording fails
        // }

        // // Start polling firmware update status every 5 seconds
        setFirwareUpdateSuccess('initiated');
        setIsFirmwareUpdating(true);
        setIsLoading(false); // Stop upload loader, polling will handle progress
        setIsProgressCardDismissed(false);
        dispatch(addFirmwareUpdateDevice(device.id));

        // Store file name in Redux progress for persistence across navigation
        dispatch(setFirmwareUpdateProgress(device.id, {
          status: 'initiated',
          fileName: selectedFile.name,
          startTime: upgradeStartedAt,
          firmwareVersion: firmwareVersionFromFile || null
        }));

        startPollingFirmwareStatus();


        // Don't set firwareUpdateSuccess here - wait for polling to detect 'success'
        // Polling will handle status updates and UI changes
      } else {
        Notification.error('Update Failed', 'Failed to update firmware. Please try again.');
        setIsLoading(false);
        setFirwareUpdateError('Failed to update firmware. Please try again.');
        return;
      }

      // Device will reboot after firmware update, so wait and retry fetching device data
      // Wait initial delay for device to start rebooting
      //await new Promise(resolve => setTimeout(resolve, 1000)); // 10 seconds initial wait

      //  Retry fetching device data with exponential backoff
      // //  Device typically takes 30-60 seconds to reboot and come back online
      //   let retryCount = 0;
      //   const maxRetries = 3; // 12 retries = ~2 minutes total
      //   const baseDelay = 10000; // 10 seconds base delay

      //   const retryRefetch = async () => {
      //     while (retryCount < maxRetries) {
      //       try {
      //         // Refetch device data
      //         dispatch(fetchDevices({}));

      //         // Wait before next retry (exponential backoff: 10s, 10s, 10s...)
      //         await new Promise(resolve => setTimeout(resolve, baseDelay));
      //         retryCount++;

      //         // If auto-refresh is available, restart it after a few retries
      //         if (retryCount === 3 && startAutoRefresh) {
      //           startAutoRefresh();
      //         }
      //       } catch (error) {
      //         console.error('Error during device refetch retry:', error);
      //       }
      //     }
      //   };

      //   // Start retry process in background (don't await to block UI)
      //  retryRefetch();

      // Clear selected file
      //setSelectedFile(null);

      // setConnectionTypeMismatch(null);
      //  setFirmwareVersionFromFile(null);
    } catch (error) {
      console.error('Firmware update error:', error);
      Notification.error('Update Failed', 'Failed to update firmware. Please try again.');
      setFirwareUpdateSuccess('failed');
      setFirwareUpdateError('Failed to update firmware. Please try again.');
      setIsLoading(false);
      setIsFirmwareUpdating(false);
      // Remove from Redux if present
      if (firmwareUpdateDevices.includes(device.id)) {
        dispatch(removeFirmwareUpdateDevice(device.id));
      }
    }
    // Note: For success case, don't set isLoading to false here
    // The useEffect will handle it based on firmware_update.data status ('initiated' -> 'success')
  };

  // Check for firmware update error from device
  // Error can come from device.firmware_update.error or device.firmware_update.data.error
  //const firmwareUpdateError = (device as any).firmware_update?.error || (device as any).firmware_update?.data?.error || null;
  const hasFirmwareError = false;//firmwareUpdateError !== null && firmwareUpdateError.includes("Firmware cannot be updated while USB is inserted");


  // Monitor firmware update status from device (per device ID) and start polling if needed
  // useEffect(() => {
  //   // Only monitor for the current device
  //   const currentDeviceId = device.id;
  //   const firmwareUpdateData = (device as any).deviceData?.firmware_update?.data;

  //   // Check Redux state first - if device is in the array, it's updating
  //   const isInReduxArray = firmwareUpdateDevices.includes(currentDeviceId);

  //   console.log('device.id', currentDeviceId);
  //   console.log('firmwareUpdateData', firmwareUpdateData);
  //   console.log('isInReduxArray', isInReduxArray);

  //   // If device is in Redux array but polling isn't running, start polling
  //   if (isInReduxArray && !pollingIntervalRef.current) {
  //     startPollingFirmwareStatus();
  //   }

  //   if (firmwareUpdateData) {
  //     const dataLower = String(firmwareUpdateData).toLowerCase();

  //     if (dataLower === 'initiated') {
  //       // Firmware update is in progress
  //       setIsFirmwareUpdating(true);
  //       setIsLoading(false); // Stop upload loader, start progress loader
  //       // Add device ID to Redux array if not already present
  //       if (!isInReduxArray) {
  //         dispatch(addFirmwareUpdateDevice(currentDeviceId));
  //       }
  //       // Start polling if not already running
  //       if (!pollingIntervalRef.current) {
  //         startPollingFirmwareStatus();
  //       }
  //     } else if (dataLower === 'success' || dataLower === 'completed') {
  //       // Firmware update completed successfully
  //       setIsFirmwareUpdating(false);
  //       setIsLoading(false);
  //       setFirwareUpdateSuccess(true);
  //       setFirwareUpdateError(null);
  //       // Stop polling
  //       stopPollingFirmwareStatus();
  //       // Remove device ID from Redux array
  //       if (isInReduxArray) {
  //         dispatch(removeFirmwareUpdateDevice(currentDeviceId));
  //       }
  //       // Refresh device data
  //       refetchDevice();
  //     } else if (dataLower === 'failed' || dataLower === 'error') {
  //       // Firmware update failed
  //       setIsFirmwareUpdating(false);
  //       setIsLoading(false);
  //       setFirwareUpdateSuccess(false);
  //       const errorMsg = (device as any).deviceData?.firmware_update?.error || 'Firmware update failed';
  //       setFirwareUpdateError(errorMsg);
  //       // Stop polling
  //       stopPollingFirmwareStatus();
  //       // Remove device ID from Redux array
  //       if (isInReduxArray) {
  //         dispatch(removeFirmwareUpdateDevice(currentDeviceId));
  //       }
  //     } else {
  //       // Other states - stop loading
  //       setIsFirmwareUpdating(false);
  //       setIsLoading(false);
  //     }
  //   } else {
  //     // No firmware update data - check Redux state
  //     if (isInReduxArray) {
  //       // Device is in Redux array but no data yet - keep loading state and start polling
  //       setIsFirmwareUpdating(true);
  //       setIsLoading(false);
  //       if (!pollingIntervalRef.current) {
  //         startPollingFirmwareStatus();
  //       }
  //     } else {
  //       // Not in Redux and no data - stop loading and polling
  //       setIsFirmwareUpdating(false);
  //       stopPollingFirmwareStatus();
  //       // Only clear isLoading if we're not in a success state
  //       if (firwareUpdateSuccess === null) {
  //         setIsLoading(false);
  //       }
  //     }
  //   }
  // }, [device, device.id, firmwareUpdateDevices, dispatch]); // Include firmwareUpdateDevices to react to Redux changes


  useEffect(() => {
    if (firwareUpdateSuccess) {
      //  console.log('firwareUpdateSuccess', firwareUpdateSuccess);
      dispatch(fetchDevices({}));
    }
  }, [firwareUpdateSuccess]);
  return (
    <div>

      {/* Check if device is running a version that doesn't support firmware updates */}
      {/* Don't show warning if: version is "-" (device reporting), version is empty/null, or firmware update is in progress */}
      {!deviceVersionSupported &&
        deviceCurrentVersion !== '-' ? (
        <div className="bg-[rgba(235,28,55,0.06)] border border-[rgba(235,28,55,0.2)] rounded-md p-4 mb-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-4 w-4 text-hero-red flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-[13px] font-semibold text-hero-red mb-1">Firmware Update Not Supported</h3>
              <p className="text-[13px] text-hero-red mb-2">
                The device is running firmware version <strong>{deviceCurrentVersion}</strong>, which does not support firmware updates.
              </p>
              <p className="text-[13px] text-hero-red">
                Please update the device to version <strong>{deviceMinRequiredVersion}</strong> or higher to enable firmware update functionality.
              </p>
              <p className="text-[11px] text-hero-red mt-2 opacity-90">
                Current device version is behind the minimum required version. Firmware update is not supported for this version.
              </p>
            </div>
          </div>
        </div>
      ) : null}

      {deviceCurrentVersion === '-' && isFirmwareUpdating && (
        <div className="bg-[rgba(0,39,63,0.06)] border border-[rgba(0,39,63,0.15)] rounded-md p-4 mb-4">
          <div className="flex items-start gap-3">
            <Loader2 className="h-4 w-4 text-blueprint-blue flex-shrink-0 mt-0.5 animate-spin" />
            <div className="flex-1">
              <h3 className="text-[13px] font-semibold text-blueprint-blue mb-1">Device is rebooting</h3>
              <p className="text-[13px] text-text-grey">
                The device is rebooting. Please wait for the device to finish rebooting.
              </p>
            </div>
          </div>
        </div>
      )}


      {/* Display firmware update error if present */}
      {deviceVersionSupported ? (
        <Card>
          <CardContent>
            <Spin spinning={isLoading} tip={isLoading ? "Uploading and updating firmware..." : ""}>
              <div className="space-y-2">
                {/* Firmware Update Status Message - Show from Redux state even when switching tabs */}
                {/* Show progress card if there's active progress in Redux OR if there's a selected file with status */}
                {((firmwareUpdateProgress && (firmwareUpdateProgress.status === 'initiated' || firmwareUpdateProgress.status === 'in_progress' || firmwareUpdateProgress.status === 'completed' || firmwareUpdateProgress.status === 'failed')) ||
                  (firwareUpdateSuccess != null)) && !isProgressCardDismissed && (() => {
                      const isInProgress = firmwareUpdateProgress?.status === 'initiated' || firmwareUpdateProgress?.status === 'in_progress' || isFirmwareUpdating;
                      const isSuccess = firmwareUpdateProgress?.status === 'completed' || firwareUpdateSuccess === 'success';
                      const isFailed = firmwareUpdateProgress?.status === 'failed' || firwareUpdateError;
                      const tone =
                        isSuccess ? 'bg-[rgba(31,154,58,0.08)] border-[rgba(31,154,58,0.2)] text-galvanizing-green' :
                        isFailed ? 'bg-[rgba(235,28,55,0.06)] border-[rgba(235,28,55,0.2)] text-hero-red' :
                        isInProgress ? 'bg-[rgba(0,39,63,0.06)] border-[rgba(0,39,63,0.15)] text-blueprint-blue' :
                        'bg-page-bg border-[#E2E5EA] text-text-grey';
                      return (
                        <div className={`relative rounded-md border p-4 ${tone}`}>
                          {!isInProgress && (
                            <button
                              onClick={handleDismissProgressCard}
                              className="absolute top-2 right-2 text-heritage-grey hover:text-text-grey flex-shrink-0 transition-colors z-10"
                              aria-label="Close"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          )}
                          <div className="flex items-start gap-3">
                            {isInProgress ? (
                              <Loader2 className="h-4 w-4 text-blueprint-blue flex-shrink-0 mt-0.5 animate-spin" />
                            ) : isSuccess ? (
                              <CheckCircle className="h-4 w-4 text-galvanizing-green flex-shrink-0 mt-0.5" />
                            ) : isFailed ? (
                              <XCircle className="h-4 w-4 text-hero-red flex-shrink-0 mt-0.5" />
                            ) : null}
                            <div className="flex-1 pr-8">
                              <p className="text-[13px] font-medium">
                                {isInProgress ? (
                                  <span className="flex items-center gap-2">
                                    Firmware update in progress
                                    <span className="inline-flex items-center gap-1">
                                      <span className="w-1.5 h-1.5 bg-blueprint-blue rounded-full animate-bounce"></span>
                                      <span className="w-1.5 h-1.5 bg-blueprint-blue rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></span>
                                      <span className="w-1.5 h-1.5 bg-blueprint-blue rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></span>
                                    </span>
                                  </span>
                                ) : isSuccess ? (
                                  'Firmware updated successfully'
                                ) : isFailed ? (
                                  firmwareUpdateProgress?.error || firwareUpdateError || 'Firmware update failed'
                                ) : (
                                  'Firmware update status unknown'
                                )}
                              </p>
                              {(firmwareUpdateProgress?.fileName || selectedFile) && (
                                <p className="text-[11px] mt-1 opacity-90">
                                  File: {firmwareUpdateProgress?.fileName || selectedFile?.name}
                                </p>
                              )}
                              {firmwareUpdateProgress?.startTime && (
                                <p className="text-[11px] mt-1 opacity-80">
                                  Started: {new Date(firmwareUpdateProgress.startTime).toLocaleString()}
                                </p>
                              )}
                              {(firmwareUpdateProgress?.firmwareVersion || firmwareVersionFromFile) && (
                                <p className="text-[11px] mt-1 opacity-80">
                                  Uploaded Version: {firmwareVersionFromFile || firmwareUpdateProgress?.firmwareVersion}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })()}

                {selectedFile && (
                  <div className="mt-4 space-y-3">
                    <div className="p-4 bg-page-bg rounded-md border border-[#E2E5EA]">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <FileText className="h-5 w-5 text-heritage-grey flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="text-[13px] font-medium text-blueprint-blue truncate" title={selectedFile.name}>
                              {selectedFile.name}
                            </div>
                            <div className="text-[11px] text-heritage-grey mt-1">
                              {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                              {firmwareVersionFromFile && (
                                <span className="ml-2">• Version: {firmwareVersionFromFile}</span>
                              )}
                            </div>
                          </div>
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          disabled={(isLoading || isFirmwareUpdating) || !firmwareCompatible}
                          onClick={handleRemoveFile}
                          className="ml-3 border-[rgba(235,28,55,0.2)] text-hero-red hover:text-hero-red hover:bg-[rgba(235,28,55,0.06)] flex-shrink-0"
                        >
                          <X className="h-4 w-4 mr-1" />
                          Remove
                        </Button>
                      </div>
                    </div>

                    {/* Firmware Compatibility Warning */}
                    {selectedFile && !firmwareCompatible && firmwareVersionFromFile && minRequiredVersion && fileProcessType && (
                      <div className="bg-[rgba(230,161,0,0.08)] border border-[rgba(230,161,0,0.2)] rounded-md p-4">
                        <div className="flex items-start gap-3">
                          <AlertTriangle className="h-4 w-4 text-[#B38600] flex-shrink-0 mt-0.5" />
                          <div className="flex-1">
                            <h3 className="text-[13px] font-semibold text-[#B38600] mb-2">Firmware Version Incompatible</h3>
                            <p className="text-[13px] text-text-grey">
                              {fileProcessType === 'welding' ? (
                                <>Welding device: Firmware version must be ≥ <strong>{MIN_WELDING_FIRMWARE}</strong></>
                              ) : fileProcessType === 'coating' ? (
                                <>Coating device: Firmware version must be ≥ <strong>{MIN_COATING_FIRMWARE}</strong></>
                              ) : null}
                            </p>
                            <p className="text-[11px] text-heritage-grey mt-2">
                              Selected firmware version: <strong>{firmwareVersionFromFile}</strong>
                            </p>
                            <p className="text-[11px] text-heritage-grey mt-1">
                              The Update Firmware button is disabled because the selected firmware version does not meet the minimum requirement.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="flex justify-center mt-8 gap-2">
                {!selectedFile && !hasFirmwareError && (
                  <div className="align-center">
                    <div className="flex justify-center">
                      <FileUpload
                        onFileSelect={handleFileSelect}
                        accept=".zip"
                        buttonText="Upload Firmware File (ZIP)"
                        variant="outline"
                        size="default"
                        showFileList={false}
                        disabled={isLoading || !isOnline}
                        selectedFiles={selectedFile ? [selectedFile] : []}
                      />
                    </div>
                  </div>
                )}
                {selectedFile && isOnline && firmwareCompatible && hasFileBeenValidated && (
                  <Button
                    onClick={handleUpdateFirmware}
                    disabled={(isLoading || isFirmwareUpdating) || !firmwareCompatible}
                    loading={isLoading || isFirmwareUpdating}
                    loadingText={isFirmwareUpdating ? "Firmware updating..." : "Updating..."}
                  >
                    Update Firmware
                  </Button>
                )}
              </div>

              {/* Show compatibility info message only when NO file is selected */}
              {isOnline && !hasFileBeenValidated && (
                <div className="mt-8 space-y-4">
                  {/* Firmware Compatibility Info Message */}
                  {selectedFile && minRequiredVersion && (
                    <div className="bg-[rgba(230,161,0,0.08)] border border-[rgba(230,161,0,0.2)] rounded-md p-4">
                      <div className="flex items-start gap-3">
                        <Info className="h-4 w-4 text-[#B38600] flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <h3 className="text-[13px] font-semibold text-[#B38600] mb-2">Firmware Compatibility Check</h3>
                          <div className="text-[13px] text-text-grey">
                            {validdationfileError !== null ? (
                              <div className="text-hero-red font-medium">{validdationfileError?.error}</div>
                            ) : (
                              <>
                                {processType?.toLowerCase() === 'welding' ? (
                                  <>Welding device: Firmware version must be ≥ <strong>{MIN_WELDING_FIRMWARE}</strong></>
                                ) : processType?.toLowerCase() === 'coating' ? (
                                  <>Coating device: Firmware version must be ≥ <strong>{MIN_COATING_FIRMWARE}</strong></>
                                ) : null}
                              </>
                            )}
                          </div>
                          <p className="text-[11px] text-heritage-grey mt-2">
                            The firmware file version will be checked against the minimum requirement when you select a file.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Firmware Filename Format */}
                  <div className="bg-[rgba(0,39,63,0.06)] border border-[rgba(0,39,63,0.15)] rounded-md p-4">
                    <div className="flex items-start gap-3">
                      <Info className="h-4 w-4 text-blueprint-blue flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <h3 className="text-[13px] font-semibold text-blueprint-blue mb-3">Firmware Filename Format</h3>
                          {processType?.toLowerCase() === 'coating' ? (
                            <div className="space-y-2">
                              <p className="text-[13px] text-text-grey">
                                <strong>Format:</strong> <code className="bg-white px-2 py-1 rounded text-xs font-mono">data360-coating-{`{env}`}-v{`{version}`}.zip</code>
                              </p>
                              <p className="text-[11px] text-heritage-grey">
                                <strong>Example:</strong> <code className="bg-white px-2 py-1 rounded text-xs font-mono">data360-coating-{environment}-v1.0.0.zip</code>
                              </p>
                              <ul className="text-[11px] text-heritage-grey mt-2 space-y-1 list-disc list-inside">
                                <li>Must start with <code className="bg-white px-1 py-0.5 rounded text-xs font-mono">data360-coating-</code></li>
                                <li>Environment must match: <strong>{environment}</strong></li>
                                <li>Version must start with <code className="bg-white px-1 py-0.5 rounded text-xs font-mono">v</code> (e.g., v1.0.0)</li>
                                <li>File must have <code className="bg-white px-1 py-0.5 rounded text-xs font-mono">.zip</code> extension</li>
                              </ul>
                            </div>
                          ) : processType?.toLowerCase() === 'welding' ? (
                            <div className="space-y-3">
                              {connectionType === 'can' ? (
                                <>
                                  <p className="text-[13px] text-text-grey">
                                    <strong>Format (CAN):</strong> <code className="bg-white px-2 py-1 rounded text-xs font-mono">data360-can-{`{env}`}-v{`{version}`}-{`{platform}`}.zip</code>
                                  </p>
                                  <p className="text-[11px] text-heritage-grey">
                                    <strong>Example:</strong> <code className="bg-white px-2 py-1 rounded text-xs font-mono">data360-can-{environment}-v1.0.0-{platform}.zip</code>
                                  </p>
                                </>
                              ) : (
                                <>
                                  <p className="text-[13px] text-text-grey">
                                    <strong>Format (Serial):</strong> <code className="bg-white px-2 py-1 rounded text-xs font-mono">data360-{`{env}`}-v{`{version}`}-{`{platform}`}.zip</code>
                                  </p>
                                  <p className="text-[11px] text-heritage-grey">
                                    <strong>Example:</strong> <code className="bg-white px-2 py-1 rounded text-xs font-mono">data360-{environment}-v1.0.0-{platform}.zip</code>
                                  </p>
                                </>
                              )}
                              <ul className="text-[11px] text-heritage-grey mt-2 space-y-1 list-disc list-inside">
                                <li>Must start with <code className="bg-white px-1 py-0.5 rounded text-xs font-mono">data360</code> {connectionType === 'can' && '(or data360-can for CAN devices)'}</li>
                                <li>Environment must match: <strong>{environment}</strong></li>
                                <li>Version must start with <code className="bg-white px-1 py-0.5 rounded text-xs font-mono">v</code> (e.g., v1.0.0)</li>
                                <li>Platform must match: <strong>{platform}</strong></li>
                                <li>File must have <code className="bg-white px-1 py-0.5 rounded text-xs font-mono">.zip</code> extension</li>
                              </ul>
                            </div>
                          ) : (
                            <div className="space-y-2">
                              <p className="text-[13px] text-text-grey">
                                Please ensure the firmware filename follows the correct format based on your device type.
                              </p>
                              <p className="text-[11px] text-heritage-grey">
                                <strong>Current Environment:</strong> {environment}
                              </p>
                              {platform !== '-' && (
                                <p className="text-[11px] text-heritage-grey">
                                  <strong>Platform:</strong> {platform}
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                  </div>
                </div>
              )}
            </Spin>
          </CardContent>
        </Card>
      ) : null}

    </div>
  );
};

